import { CommerceAPI } from "commerce-api"
import { StoreModel } from "./utils"
import { Status, StatusMap } from "../types/commerce"
import { RootStore } from "./RootStore"
import { makeObservable, observable, action, computed, flow, flowResult, reaction } from "mobx"
import { getAppOrigin } from '@salesforce/pwa-kit-react-sdk/utils/url'
import { BookRapidDeliveryResponse, RapidDeliveryStoreInitialState, RapidDeliveryReservation } from '../types/store/rapidDelivery'
import { RapidBasketCapacityState } from '../types/store/basket'

export class RapidDeliveryStore implements StoreModel {
  api: CommerceAPI
  status: Status
  rootStore: RootStore
  isRapidDeliveryAvailableForPostcode: boolean
  rapidDeliveryAvailabilityResolved: boolean
  rapidDeliveryShippingCost: number | null
  rapidDeliveryMaxItems: number | null
  rapidDeliveryMaxWeight: number | null
  rapidDeliveryMinDelivery: number | null
  rapidDeliveryBlockedSchemas: string[]
  rapidDeliveryRulesLoaded: boolean
  rapidBasketCapacityState: RapidBasketCapacityState
  lastShownRapidBasketCapacityState: RapidBasketCapacityState
  rapidBasketCapacityModalOpen: boolean
  rapidBasketAnimationTick: number
  rapidDeliveryETA: string | null
  rapidDeliveryETABasketId: string | null
  rapidDeliveryETAPostcode: string | null
  constructor(rootStore: RootStore, initialState?: RapidDeliveryStoreInitialState) {
    this.api = rootStore.api
    this.status = initialState?.status ?? StatusMap.IDLE
    this.isRapidDeliveryAvailableForPostcode = initialState?.isRapidDeliveryAvailableForPostcode || false
    this.rapidDeliveryAvailabilityResolved = initialState?.rapidDeliveryAvailabilityResolved ?? false
    this.rapidDeliveryShippingCost = initialState?.rapidDeliveryShippingCost || null
    this.rapidDeliveryMaxItems = initialState?.rapidDeliveryMaxItems || null
    this.rapidDeliveryMaxWeight = initialState?.rapidDeliveryMaxWeight || null
    this.rapidDeliveryMinDelivery = initialState?.rapidDeliveryMinDelivery || null
    this.rapidDeliveryBlockedSchemas = initialState?.rapidDeliveryBlockedSchemas || []
    this.rapidDeliveryRulesLoaded = initialState?.rapidDeliveryRulesLoaded ?? false
    this.rapidBasketCapacityState = initialState?.rapidBasketCapacityState || 'none'
    this.lastShownRapidBasketCapacityState = initialState?.lastShownRapidBasketCapacityState || 'none'
    this.rapidBasketCapacityModalOpen = initialState?.rapidBasketCapacityModalOpen || false
    this.rapidBasketAnimationTick = initialState?.rapidBasketAnimationTick || 0
    this.rapidDeliveryETA = initialState?.rapidDeliveryETA ?? null
    this.rapidDeliveryETABasketId = initialState?.rapidDeliveryETABasketId ?? null
    this.rapidDeliveryETAPostcode = initialState?.rapidDeliveryETAPostcode ?? null
    this.rootStore = rootStore

    reaction(
      () => ({
        basketId: this.rootStore.basketStore.basket?.basketId,
        deliverySlotId: this.rootStore.basketStore.currentDeliverySlotId,
        postcode: this.rootStore.basketStore.deliveryPostalCode,
        isRapidDeliveryAvailableForPostcode: this.isRapidDeliveryAvailableForPostcode,
      }),
      ({basketId, postcode, isRapidDeliveryAvailableForPostcode}) => {
        if (!basketId || !postcode || !isRapidDeliveryAvailableForPostcode) {
          return
        }

        flowResult(this.loadRapidDeliveryETA(postcode)).catch((error) => {
          console.error('Failed to load Rapid Delivery ETA:', error)
        })
      },
      {fireImmediately: true}
    )

    // Blocked schemas are keyed by customer group, not postcode/basket, so fetch them
      // whenever Rapid Delivery is booked but the rules haven't been loaded yet (e.g. on refresh).
      reaction(
        () => ({
          isRapidDeliveryBooked: this.isRapidDeliveryBooked,
          rapidDeliveryRulesLoaded: this.rapidDeliveryRulesLoaded,
          customerGroup: this.rapidDeliveryCustomerGroup,
        }),
        ({isRapidDeliveryBooked, rapidDeliveryRulesLoaded, customerGroup}) => {
          if (isRapidDeliveryBooked && !rapidDeliveryRulesLoaded && customerGroup) {
            flowResult(this.loadRapidDeliveryBlockedSchemas(customerGroup)).catch((error) => {
              console.error('Failed to load Rapid Delivery blocked schemas:', error)
            })
          }
        },
        {fireImmediately: true}
      )

    makeObservable(this, {
      status: observable,
      isRapidDeliveryAvailableForPostcode: observable,
      rapidDeliveryAvailabilityResolved: observable,
      rapidBasketCapacityState: observable,
      lastShownRapidBasketCapacityState: observable,
      rapidBasketCapacityModalOpen: observable,
      rapidBasketAnimationTick: observable,
      rapidDeliveryETA: observable,
      rapidDeliveryETABasketId: observable,
      rapidDeliveryETAPostcode: observable,
      setRapidDeliveryAvailability: action.bound,
      setRapidDeliveryAvailabilityResolved: action.bound,
      resetRapidDeliveryETA: action.bound,
      setRapidBasketCapacityState: action.bound,
      setLastShownRapidBasketCapacityState: action.bound,
      setRapidBasketCapacityModalOpen: action.bound,
      triggerRapidBasketAnimation: action.bound,
      updateRapidBasketCapacityAlert: action.bound,
      resetRapidBasketCapacityAlert: action.bound,
      bookRapidDelivery: flow.bound,
      rapidDeliveryShippingCost: observable,
      rapidDeliveryMaxItems: observable,
      rapidDeliveryMaxWeight: observable,
      rapidDeliveryMinDelivery: observable,
      rapidDeliveryBlockedSchemas: observable,
      rapidDeliveryRulesLoaded: observable,
      setRapidDeliveryBlockedSchemas: action.bound,
      setRapidDeliveryRulesLoaded: action.bound,
      isBasketOverRapidDeliveryItemLimit: computed,
      isBasketOverRapidDeliveryWeightLimit: computed,
      rapidDeliveryETAForCurrentBasket: computed,
      getRapidDeliveryETA: flow.bound,
      loadRapidDeliveryETA: flow.bound,
      getBlockedSchemas: flow.bound,
      loadRapidDeliveryBlockedSchemas: flow.bound
    })
  }

  setStatus(value: Status) {
    this.status = value
  }

  setRapidDeliveryAvailability(value: boolean) {
    this.isRapidDeliveryAvailableForPostcode = value
  }

  setRapidDeliveryAvailabilityResolved(value: boolean) {
    this.rapidDeliveryAvailabilityResolved = value
  }

  resetRapidDeliveryETA() {
    this.rapidDeliveryETA = null
    this.rapidDeliveryETABasketId = null
    this.rapidDeliveryETAPostcode = null
  }

  setRapidDeliveryMaxItems(value: number | null) {
    this.rapidDeliveryMaxItems = value
  }
  
  setRapidDeliveryMaxWeight(value: number | null) {
    this.rapidDeliveryMaxWeight = value
  }

  setRapidDeliveryMinDelivery(value: number | null) {
    this.rapidDeliveryMinDelivery = value
  }

  setRapidDeliveryShippingCost(value: number | null) {
    this.rapidDeliveryShippingCost = value
  }

  setRapidDeliveryBlockedSchemas(schemas: string[] = []) {
    this.rapidDeliveryBlockedSchemas = schemas
  }

  setRapidDeliveryRulesLoaded(value: boolean) {
    this.rapidDeliveryRulesLoaded = value
  }

  setRapidBasketCapacityState(state: RapidBasketCapacityState) {
    this.rapidBasketCapacityState = state
  }

  setLastShownRapidBasketCapacityState(state: RapidBasketCapacityState) {
    this.lastShownRapidBasketCapacityState = state
  }

  setRapidBasketCapacityModalOpen(open: boolean) {
    this.rapidBasketCapacityModalOpen = open
  }

  triggerRapidBasketAnimation() {
    this.rapidBasketAnimationTick += 1
  }

  resetRapidBasketCapacityAlert() {
    this.setRapidBasketCapacityState('none')
    this.setLastShownRapidBasketCapacityState('none')
    this.setRapidBasketCapacityModalOpen(false)
  }

  resolveRapidBasketCapacityState(): RapidBasketCapacityState {
    if (!this.rootStore.basketStore.basket?.c_isRapidDeliverySelected) {
      return 'none'
    }

    const { progress } = this.rootStore.basketStore.basketLimitThreshold ?? {}
    if (typeof progress !== 'number') return 'none'
    if (progress >= 100) return 'fullLimit'
    if (progress > 90) return 'nearLimit'
    return 'none'
  }

  updateRapidBasketCapacityAlert(previousBasketCount: number) {
    const basket = this.rootStore.basketStore.basket
    const currentBasketCount = basket?.productQuantityTotal ?? 0
    const nextCapacityState = this.resolveRapidBasketCapacityState()

    if (nextCapacityState === 'fullLimit') {
      this.setRapidBasketCapacityState(nextCapacityState)
      this.setRapidBasketCapacityModalOpen(true)
      this.setLastShownRapidBasketCapacityState(nextCapacityState)
      this.triggerRapidBasketAnimation()
      return
    }

    if (currentBasketCount <= previousBasketCount) {
      return
    }

    this.setRapidBasketCapacityState(nextCapacityState)

    if (nextCapacityState === 'none') {
      this.setRapidBasketCapacityModalOpen(false)
      this.setLastShownRapidBasketCapacityState('none')
      return
    }

    if (this.lastShownRapidBasketCapacityState !== nextCapacityState) {
      this.setRapidBasketCapacityModalOpen(true)
      this.setLastShownRapidBasketCapacityState(nextCapacityState)
      this.triggerRapidBasketAnimation()
    }
  }

  *bookRapidDelivery() {
    if (this.isRapidDeliveryBooked) {
      this.resetRapidDeliveryETA()
    }
    const response: Response = yield fetch(
      `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Rapid-BookDelivery`,
      {
        method: 'POST',
        credentials: 'include'
      }
    )
    const result: BookRapidDeliveryResponse = yield response.json()

    if (result.basket) {
      this.rootStore.basketStore.getOrCreateBasket(result.basket)
      this.rootStore.customerStore.setCustomerGroups(result.c_customerGroups)
      this.rootStore.globalStore.setSelectedRegion()
    }

    return result;
  }

  get isBasketOverRapidDeliveryItemLimit() {
    if (this.rapidDeliveryMaxItems === null) return false
    return (this.rootStore.basketStore.basket?.productQuantityTotal ?? 0) > this.rapidDeliveryMaxItems
  }

  get isBasketOverRapidDeliveryWeightLimit() {
    if (this.rapidDeliveryMaxWeight === null) return false

    const basketWeightInGrams = this.rootStore.basketStore.basket?.c_weight
    if (basketWeightInGrams === null || basketWeightInGrams === undefined) return false

    return basketWeightInGrams > this.rapidDeliveryMaxWeight * 1000
  }

  get isRapidDeliveryAvailable() {
    return this.rootStore.globalStore.customSitePreferences?.enableRapidDelivery as boolean
  }

  get rapidDeliverySoftReserveTimeLimit() {
    return this.rootStore.globalStore.customSitePreferences?.rapidDeliverySoftReserveTime as number
  }

  get rapidDeliveryFallback() {
    return this.rootStore.globalStore.customSitePreferences?.rapidDeliveryFallback as string
  }

  get rapidDeliveryCustomerGroup() {
    return this.rootStore.globalStore.customSitePreferences?.rapidDeliveryCustomerGroup as string
  }

  get rapidDeliveryServiceUnavailableMessage() {
    return this.rootStore.globalStore.customSitePreferences?.rapidDeliveryServiceUnavailable as boolean
  }

  get rapidDeliveryPricebookEnabled() {
    return this.rootStore.globalStore.customSitePreferences?.rapidDeliveryPricebookEnabled as boolean
  }

  get isRapidDeliveryBooked() {
    return this.rootStore.basketStore.basket?.c_isRapidDeliverySelected ?? false
  }

  // Whether product prices should be read from rapidMatrixPrices instead of matrixPrices
  get isRapidPricingActive() {
    return this.isRapidDeliveryBooked && this.rapidDeliveryPricebookEnabled
  }

  get rapidDeliveryDeliveryEstimate() {
    return this.rootStore.globalStore.customSitePreferences?.rapidDeliveryDeliveryEstimate as number ?? 60
  }

  get rapidDeliveryETAForCurrentBasket() {
    return this.rapidDeliveryETABasketId === this.rootStore.basketStore.basket?.basketId
      ? this.rapidDeliveryETA ?? this.rapidDeliveryDeliveryEstimate
      : this.rapidDeliveryDeliveryEstimate
  }

  get isRapidSponsoredProductsHidden() {
    return this.rootStore.globalStore.customSitePreferences?.rapidDeliveryHideSponsoredProducts as boolean
  }

  get rapidDeliveryReservation(): RapidDeliveryReservation | null {
    const basket = this.rootStore.basketStore.basket
    if (!basket?.c_slotExpiryTime || !this.isRapidDeliveryBooked) {
      return null
    }
    return {
      expiryTime: basket.c_slotExpiryTime,
      windowStartTime: basket.c_windowStartTime || null,
      windowEndTime: basket.c_windowEndTime || null,
      shippingCost: basket.shippingItems?.[0].c_deliveryCharge ?? null
    }
  }

  *getRapidDeliveryETA(postcode: string){
    const res: Response = yield fetch(`${getAppOrigin()}/api/getDeliveryETA`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: this.api.auth.authToken,
      },
      body: JSON.stringify({ postcode: postcode })
    })

    const json = (yield res.json()) as Record<string, unknown>

    return json
  }

  *loadRapidDeliveryETA(postcode: string, basketId = this.rootStore.basketStore.basket?.basketId) {
    if (!postcode || !basketId) {
      return null
    }

    if (
      this.rapidDeliveryETABasketId === basketId &&
      this.rapidDeliveryETAPostcode === postcode &&
      this.rapidDeliveryETA
    ) {
      return this.rapidDeliveryETA
    }

    if (!this.isRapidDeliveryAvailableForPostcode) return null

    const result: Record<string, unknown> = yield this.getRapidDeliveryETA(postcode)
    const etaSeconds = Number(result.eta)
    if (!Number.isFinite(etaSeconds)) {
      throw new Error('Rapid Delivery ETA was not provided')
    }

    const etaMinutes = Math.ceil(etaSeconds / 60)
    this.rapidDeliveryETA = `${etaMinutes} to ${etaMinutes + 10}`
    this.rapidDeliveryETABasketId = basketId
    this.rapidDeliveryETAPostcode = postcode
    return this.rapidDeliveryETA
  }

  *getBlockedSchemas(customerGroup: string) {
    const res: Response = yield fetch(
      `${getAppOrigin()}/api/getBlockedSchemas?customerGroup=${encodeURIComponent(customerGroup)}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          Authorization: this.api.auth.authToken,
        },
      }
    )

    const json = (yield res.json()) as Record<string, unknown>

    return json
  }

  *loadRapidDeliveryBlockedSchemas(customerGroup = this.rapidDeliveryCustomerGroup) {
    if (!customerGroup) {
      return
    }

    try {
      const result: Record<string, unknown> = yield this.getBlockedSchemas(customerGroup)
      this.setRapidDeliveryBlockedSchemas((result.blockedSchemas as string[]) ?? [])
    } catch (error) {
      console.error('Failed to load Rapid Delivery blocked schemas:', error)
      this.setRapidDeliveryBlockedSchemas([])
    } finally {
      this.setRapidDeliveryRulesLoaded(true)
    }
  }

  get asJson() {
    return {
      status: this.status,
      isRapidDeliveryBooked: this.isRapidDeliveryBooked,
      isRapidDeliveryAvailableForPostcode: this.isRapidDeliveryAvailableForPostcode,
      rapidDeliveryAvailabilityResolved: this.rapidDeliveryAvailabilityResolved,
      rapidDeliveryBlockedSchemas: this.rapidDeliveryBlockedSchemas,
      rapidDeliveryRulesLoaded: this.rapidDeliveryRulesLoaded,
      rapidDeliveryReservation: this.rapidDeliveryReservation,
      rapidBasketCapacityState: this.rapidBasketCapacityState,
      lastShownRapidBasketCapacityState: this.lastShownRapidBasketCapacityState,
      rapidBasketCapacityModalOpen: this.rapidBasketCapacityModalOpen,
      rapidBasketAnimationTick: this.rapidBasketAnimationTick,
      rapidDeliveryETA: this.rapidDeliveryETA,
      rapidDeliveryETABasketId: this.rapidDeliveryETABasketId,
      rapidDeliveryETAPostcode: this.rapidDeliveryETAPostcode,
    }
  }
}